import { PrimaryButton } from 'components/buttons/v4';
import { BrightTextarea } from 'components/forms/BrightTextarea';
import { ConfirmOrCancel } from 'components/forms/ConfirmOrCancel';
import { ModalPosition } from 'components/Modal';
import { ModalHeader } from 'components/modals/common';
import { Color } from 'core';
import { Card, CardHeader, CardHeaderContainer, CardSubHeader, PageTitle } from 'core/layout';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const CenterTitle = styled(PageTitle)`
  width: 100%;
  text-align: center;
  padding: 1em 0 0.5em;
`;

const ResultsContainer = styled.div`
  margin: 10px 0;
`;

const FailedInvitesContainer = styled(ResultsContainer)`
  color: ${Color.red};
`;

export interface AddByEmailConfig {
  title: string;
  headerText: string;
  confirmText: string;
  successText: (count: number) => string;
  failureText: (count: number) => string;
}

export interface AddByEmailResult {
  email: string;
  success: boolean;
}

interface AddByEmailProps {
  config: AddByEmailConfig;
  onClose: () => void;
  onInvite: (emails: string[]) => Promise<AddByEmailResult[]>;
  onSuccess?: (successfulEmails: string[]) => void;
  additionalControls?: React.ReactNode;
}

export const AddByEmail: React.FC<AddByEmailProps> = ({
  config,
  onClose,
  onInvite,
  onSuccess,
  additionalControls,
}) => {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState('');
  const [confirmIsPending, setConfirmIsPending] = useState(false);
  const [successfulInvites, setSuccessfulInvites] = useState<string[]>([]);
  const [failedInvites, setFailedInvites] = useState<string[]>([]);

  const handleConfirmClick = async () => {
    setConfirmIsPending(true);

    const emails = emailInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email !== '');

    try {
      const results = await onInvite(emails);
      
      const successful = results.filter(r => r.success).map(r => r.email);
      const failed = results.filter(r => !r.success).map(r => r.email);

      setSuccessfulInvites(successful);
      setFailedInvites(failed);

      if (successful.length > 0 && onSuccess) {
        onSuccess(successful);
      }
    } catch (error) {
      console.error('Error during invite process:', error);
      setFailedInvites(emails);
    }

    setConfirmIsPending(false);
    setEmailInput('');
  };

  const updateEmailInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.target.value);
  };

  const showInputForm = successfulInvites.length === 0 || failedInvites.length > 0;

  return (
    <ModalPosition top={1.5}>
      <ModalHeader>
        <CenterTitle>{config.title}</CenterTitle>
      </ModalHeader>
      <Card>
        <CardHeaderContainer>
          <CardHeader id="header">{config.headerText}</CardHeader>
          {showInputForm && <CardSubHeader>{t('Type or paste a list of individual email addresses below, separated by comma:')}</CardSubHeader>}
        </CardHeaderContainer>

        <div>
          {showInputForm && (
            <BrightTextarea
              value={emailInput}
              placeholder={t('e.g., name@example.com, name2@example.com, name3@example.com')}
              onChange={updateEmailInput}
              aria-labelledby="header"
            />
          )}
          {successfulInvites.length > 0 && (
            <ResultsContainer>
              <div>{config.successText(successfulInvites.length)}</div>
              {successfulInvites.map((invite, i) => (
                <div key={i}>{invite}</div>
              ))}
            </ResultsContainer>
          )}
          {failedInvites.length > 0 && (
            <FailedInvitesContainer>
              <div>{config.failureText(failedInvites.length)}</div>
              {failedInvites.map((invite, i) => (
                <div key={i}>{invite}</div>
              ))}
            </FailedInvitesContainer>
          )}
          {showInputForm && additionalControls}
        </div>
      </Card>
      {showInputForm && (
        <ConfirmOrCancel
          cancelText={t('Cancel')}
          confirmText={config.confirmText}
          handleCancelClick={onClose}
          handleConfirmClick={handleConfirmClick}
          confirmIsDisabled={emailInput.length === 0}
          confirmIsPending={confirmIsPending}
        />
      )}
      {!showInputForm && <PrimaryButton click={onClose}>{t('Done')}</PrimaryButton>}
    </ModalPosition>
  );
};